import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';
import { Mail, CheckCircle2, Plus, X, Send, RotateCcw, Info } from 'lucide-react';
import { Card, VStack } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon, Tooltip } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Select, TextField } from '@components/base/inputs';
import { useToast } from '@components/base/feedback';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  FieldConfig,
  SelectFieldOptions,
} from '@components/integrated/entity-form';
import {
  DataList,
  useDataList,
  dataListWorkflowRegistry,
} from '@components/integrated/data-list';
import type { DataListConfig } from '@components/integrated/data-list';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { RichTextEditor } from './RichTextEditor';
import { useClaimDataStore } from './claimDataStore';
import {
  emailService,
  type EmailTemplate,
  type ReplyToOption,
  type AttachableDocument,
} from '../api/emailService';

interface EmailCardProps {
  idSin: string;
  ses?: string;
  ncbtSin?: string;
  dossierNumber?: string;
  senderEmail?: string;
  senderName?: string;
  /**
   * Per-tenant layout overrides injected by the `withComponentRules` HOC.
   * Underscored to match the injected meta-prop convention; `undefined` at
   * baseline ⇒ both the form config and the attachments DataList config are
   * returned unchanged (byte-identical render).
   *
   * Carries up to two override buckets under one prop (the same `_layoutOverrides`
   * is fed to each surface's `applyLayoutOverrides` call; the host-aware merge
   * consumes only the bucket that matches its config shape):
   *  - `fields` — keyed by the 8 stable field keys (`replyTo`, `template`,
   *    `from`, `to`, `cc`, `attachments`, `subject`, `message`); merged into
   *    the `EntityFormConfig.fields` that drives the form.
   *  - `columns` — keyed by the attachments table's stable column key
   *    (`label`); merged into the attachments `DataListConfig.columns` (spec
   *    Phase 3 / Target B) so an admin can rename / hide / reorder / tooltip
   *    the attachment columns. The `attachments` *field* (label "PJ") still
   *    wraps the table as its section label.
   */
  _layoutOverrides?: LayoutOverrides;
}

const SENDER_PLACEHOLDER = 'sinistres@assurance-demo.fr';

/**
 * Default placeholder / empty-trigger text for the GED attachments add-picker.
 * Lives on the `attachments` field config (top-level `placeholder`) so a
 * `setFieldPlaceholder` rule targeting the `attachments` field key can retext
 * the picker; this constant is the baseline value and the fallback used when an
 * override clears the placeholder back to nothing.
 */
const GED_PICKER_PLACEHOLDER = 'Sélectionner un document de la GED';

const NONE_REPLY_OPTION = { value: '', label: 'Aucun (nouveau message)' };
const NONE_TEMPLATE_OPTION = { value: '', label: 'Aucun modèle' };

// ---------------------------------------------------------------------------
// Declarative field config — stable keys + config-driven labels/order/tooltip.
// ---------------------------------------------------------------------------

/**
 * Stable field keys for the email composer. These are the rule contract a
 * tenant-scoped `renameField` / `hideField` / `reorderFields` / `setFieldTooltip`
 * targets; they are intentionally separate from the display labels (which live
 * in {@link buildEmailFormConfig} and are overridable). Never rename a key.
 */
const FIELD = {
  replyTo: 'replyTo',
  template: 'template',
  from: 'from',
  to: 'to',
  cc: 'cc',
  attachments: 'attachments',
  subject: 'subject',
  message: 'message',
} as const;

/**
 * Builds the declarative `EntityFormConfig` for the email composer.
 *
 * Every field's French label, `order` (10→80, reproducing the historic
 * top-to-bottom layout), `visible`, and `validation` live here — off the JSX —
 * so an admin can rename / hide / reorder / tooltip each field per tenant via
 * the rule pipeline (`applyLayoutOverrides`). The keys (`replyTo`, `template`,
 * `from`, `to`, `cc`, `attachments`, `subject`, `message`) are the stable
 * contract and equal the would-be input `name` / data identifier.
 *
 * The field **values** and every **side effect** (the on-mount fetch fan-out,
 * `De` prefill precedence, reply/template prefill, deterministic body
 * recompose, attachment add/remove, send) stay component-owned and unchanged —
 * this config drives *how fields are declared & laid out*, not the email logic.
 * Most controls are bespoke (side-effecting `Select`s, the chip `RecipientField`,
 * the attachment chip-picker, the `RichTextEditor`), so they are rendered as
 * custom slots whose **label is config-driven**; `from`/`subject` map to base
 * inputs. The `type` of a custom-slot field is the closest declarative kind for
 * rename/hide/tooltip purposes — its default base input is not used.
 *
 * `to`/`subject` mirror the imperative `canSend` gate as `validation.required`
 * (display contract a rule can read), but `canSend` remains the single submit
 * gate so the two never diverge.
 *
 * There is no EntityForm-driven submission (the send is bespoke), so `endpoint`
 * is a placeholder that is never invoked.
 */
function buildEmailFormConfig(): EntityFormConfig {
  return {
    id: 'email-card',
    endpoint: {
      // The send is bespoke (`emailService.sendEmail` via the hand-placed
      // "Envoyer" button); this placeholder satisfies the required
      // `EndpointConfig` shape and is never called.
      erpEndpoint: 'claimflow.email.noop',
      method: 'POST',
    },
    fields: {
      [FIELD.replyTo]: {
        type: 'select',
        label: 'En réponse à',
        order: 10,
        visible: true,
        options: { placeholder: 'Sélectionner un email' } as SelectFieldOptions,
      },
      [FIELD.template]: {
        type: 'select',
        label: 'Utiliser un modèle',
        order: 20,
        visible: true,
        options: { placeholder: 'Sélectionner un modèle' } as SelectFieldOptions,
      },
      [FIELD.from]: {
        type: 'email',
        label: 'De',
        order: 30,
        visible: true,
      },
      [FIELD.to]: {
        type: 'text',
        label: 'À',
        order: 40,
        visible: true,
        validation: { required: true },
      },
      [FIELD.cc]: {
        type: 'text',
        label: 'CC',
        order: 50,
        visible: true,
      },
      [FIELD.attachments]: {
        type: 'text',
        label: 'PJ',
        // The GED add-picker's placeholder lives here (top-level `placeholder`,
        // the slot `mergeFieldOverride` writes for text-like fields) so a
        // `setFieldPlaceholder` rule targeting the `attachments` field key can
        // retext it — the picker reads this merged value, not an inline literal.
        placeholder: GED_PICKER_PLACEHOLDER,
        order: 60,
        visible: true,
      },
      [FIELD.subject]: {
        type: 'text',
        label: 'Objet',
        order: 70,
        visible: true,
        validation: { required: true },
      },
      [FIELD.message]: {
        type: 'textarea',
        label: 'Message',
        order: 80,
        visible: true,
      },
    },
    actions: {
      submit: { visible: true, label: 'Envoyer' },
      cancel: { visible: false },
      reset: { visible: false },
    },
    layout: { columns: 1, gap: 'md' },
  };
}

/**
 * Resolve the config's fields into render order: keep only `visible !== false`
 * fields and sort by `order` (ascending; missing order last). This is the same
 * collect→filter→sort contract `<EntityForm.Fields/>` uses, applied here so a
 * `reorderFields` / `hideField` override takes effect across **all** fields
 * (including the custom-slot controls), not only the base-input ones — a
 * strict superset of the spec's "auto-rendered subset reorders" promise.
 */
function resolveFieldOrder(
  fields: Record<string, FieldConfig>,
): Array<[string, FieldConfig]> {
  return Object.entries(fields)
    .filter(([, field]) => field.visible !== false)
    .sort(
      (a, b) =>
        (a[1].order ?? Number.MAX_SAFE_INTEGER) -
        (b[1].order ?? Number.MAX_SAFE_INTEGER),
    );
}

/** Minimal e-mail check: non-empty and contains an "@". */
function isValidEmail(value: string): boolean {
  return value.length > 0 && value.includes('@');
}

// ---------------------------------------------------------------------------
// Attachments ("PJ") — DataList (recipe B, spec Phase 3 / Target B).
//
// The chosen-attachments list is rendered by the integrated `DataList` so a
// tenant can rename / hide / reorder / tooltip the attachment **columns** via
// runtime rules (`_layoutOverrides.columns`). It has exactly one
// tenant-meaningful column — the document label — plus a `remove` row action
// (the X affordance, NOT a data column). The GED add-picker (`Select`) stays a
// field-internal control, not a column. Data is the inline client-side
// `attachments` state (no endpoint, no totals / footer / toolbar / pagination).
// ---------------------------------------------------------------------------

/**
 * Stable column keys for the attachments table. These are the rule contract a
 * tenant-scoped `renameColumn` / `hideColumn` / `setColumnTooltip` targets;
 * separate from the display label (which lives in {@link buildAttachmentsListConfig}
 * and is overridable). Never rename a key.
 */
const ATTACHMENT_COLUMN = {
  label: 'label',
} as const;

/** Handler key for the per-row "remove attachment" action (module singleton). */
const REMOVE_ATTACHMENT_HANDLER = 'claimflow-emailRemoveAttachment';

/**
 * Per-row context carried on each attachment row under `__ctx`. The row-action
 * handler is a module singleton (registered once below), so the live
 * `removeAttachment` callback is threaded onto each row at build time — the
 * rows are rebuilt every render (see the `attachmentRows` memo) so the closure
 * stays fresh. No module-global mutable state; multi-instance safe (mirrors the
 * `__ctx` idiom in `MissingPiecesCard` / `DocumentsCard`).
 */
interface AttachmentRowContext {
  /** Remove this attachment (client-side only — drops it from `attachments`). */
  onRemove: () => void;
}

interface AttachmentRow extends AttachableDocument {
  __ctx: AttachmentRowContext;
}

function getAttachmentRowContext(
  row: Record<string, unknown>,
): AttachmentRowContext | undefined {
  return (row as Partial<AttachmentRow>).__ctx ?? undefined;
}

// Register the remove-attachment row action once at module load. It reads the
// row's `__ctx.onRemove` so the singleton always calls the *current* card's
// `removeAttachment` (the rows carry a fresh closure each render).
dataListWorkflowRegistry.registerRowActionHandler(
  REMOVE_ATTACHMENT_HANDLER,
  (row) => {
    getAttachmentRowContext(row)?.onRemove();
    return Promise.resolve();
  },
);

/**
 * Build the declarative `DataListConfig` for the chosen-attachments list. The
 * single `label` column header ("Document") lives in `column.label`
 * (display-only / overridable); `fieldMapping` maps it to the document's
 * `label`. The X remove affordance is a `rowAction` (not a column). The list
 * preserves insertion order (sorting off), shows no toolbar / pager / totals,
 * and renders every row on one page — parity with the original chip row.
 */
function buildAttachmentsListConfig(rows: AttachmentRow[]): DataListConfig {
  return {
    id: 'claimflow.emailCard.attachments',

    dataSource: {
      data: rows as unknown as Record<string, unknown>[],
      fieldMapping: { [ATTACHMENT_COLUMN.label]: 'label' },
    },

    columns: {
      [ATTACHMENT_COLUMN.label]: {
        label: 'Document',
        order: 10,
        align: 'left',
        type: 'text',
        sortable: false,
      },
    },

    actions: {
      rowActions: [
        {
          id: 'remove',
          // The X remove affordance. `DataList` renders the label as both the
          // button text and its `aria-label`; the row context makes the target
          // unambiguous (one button per attachment row).
          label: 'Retirer',
          icon: 'x',
          variant: 'ghost',
          handler: REMOVE_ATTACHMENT_HANDLER,
        },
      ],
    },

    display: {
      size: 'compact',
      hoverable: false,
    },

    sorting: {
      enabled: false,
    },

    toolbar: {
      visible: false,
    },

    // Single short, fixed list — no pagination footer.
    pagination: {
      mode: 'scroll',
      pageSize: 999_999,
    },
  };
}

// ---------------------------------------------------------------------------
// Reply prefill helpers ("En réponse à").
// ---------------------------------------------------------------------------

/** Reply separator the RichTextEditor (TipTap) can render & round-trip. */
const REPLY_SEPARATOR = "<hr><p>---- Message d'origine ----</p>";

/**
 * Subject for a reply: the origin subject prefixed with `RE: ` — but never
 * double-prefixed when it already starts with `RE:`/`Re:` (case-insensitive).
 */
function buildReplySubject(originSubject: string): string {
  const trimmed = originSubject.trim();
  return /^re\s*:/i.test(trimmed) ? trimmed : `RE: ${trimmed}`;
}

/** Escape a plain-text string for safe inclusion in HTML. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Turn the origin email body into HTML the editor can render. An HTML body is
 * passed through (TipTap drops nodes/marks it doesn't support on paste); a
 * plain-text body has its newlines converted to paragraphs.
 */
function originBodyToHtml(body: string, bodyIsHtml: boolean): string {
  if (bodyIsHtml) return body;
  const paragraphs = body
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, '<br>')}</p>`);
  return paragraphs.join('');
}

/**
 * Compose the editor body like a standard email-client reply:
 * `[editing space] + separator + [quoted origin]`. The editing space is the
 * selected template body, or an empty paragraph when no template is selected.
 * Recomputed deterministically from the latest template body + origin HTML, so
 * selecting a template and an origin in either order yields the same result.
 */
function composeReplyBody(templateBody: string, originHtml: string): string {
  if (!originHtml) return templateBody;
  const editingSpace = templateBody.trim().length > 0 ? templateBody : '<p></p>';
  return `${editingSpace}${REPLY_SEPARATOR}${originHtml}`;
}

// ---------------------------------------------------------------------------
// Field label — config-driven, with an optional tooltip affordance.
// ---------------------------------------------------------------------------

/**
 * The accessible info-icon tooltip affordance — a focusable `<button>` wrapped
 * in the shared {@link Tooltip} — identical to the one `EntityForm.Field` uses
 * for auto-rendered fields, so a `setFieldTooltip` override renders the same
 * way regardless of which control hosts the field. Returns `null` when there is
 * no tooltip, so fields without one are byte-identical to the pre-migration
 * markup.
 */
function FieldTooltipIcon({
  name,
  label,
  tooltip,
}: Readonly<{ name: string; label: string; tooltip?: string }>) {
  if (!tooltip) return null;
  return (
    <Tooltip content={tooltip}>
      <button
        type="button"
        className="inline-flex items-center justify-center rounded-full p-0.5 text-secondary-500 transition-colors hover:text-secondary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
        aria-label={`${label} — more information`}
        data-testid={`email-field-tooltip-${name}`}
      >
        <Icon icon={Info} size="sm" aria-hidden />
      </button>
    </Tooltip>
  );
}

/**
 * Renders a custom-slot field's label from config (the same `<Text as="label">`
 * the pre-migration JSX used) followed by the optional {@link FieldTooltipIcon}.
 * Used by the controls that own their own label markup (recipients, attachments,
 * message). With no tooltip and not required, only the label renders —
 * byte-identical to the pre-migration inline `<Text as="label">`.
 *
 * When `required` is true, the shared red asterisk affordance (the exact markup
 * the base inputs and the group inputs render — `GroupLabel` / `InputLabel`)
 * trails the label, so a config-driven `validation.required` (default or a
 * `requireField` rule) surfaces the same indicator across base- and custom-slot
 * fields. The asterisk is `aria-hidden` (display-only); the required *contract*
 * lives in config and the submit gate (`canSend`), not in inline error text.
 */
function FieldLabel({
  name,
  label,
  tooltip,
  required = false,
}: Readonly<{ name: string; label: string; tooltip?: string; required?: boolean }>) {
  return (
    <span className="flex items-center gap-1">
      <Text as="label" size="sm" weight="medium" className="text-secondary-700">
        {label}
        {required && (
          <span className="ml-0.5 text-error-700" aria-hidden="true">
            *
          </span>
        )}
      </Text>
      <FieldTooltipIcon name={name} label={label} tooltip={tooltip} />
    </span>
  );
}

/**
 * Wraps a base input (`Select`/`TextField`, which render their own internal
 * label) so its config-defined tooltip surfaces via the shared affordance,
 * anchored top-right over the field — mirroring `EntityForm.Field`. When there
 * is no tooltip, the input is returned bare (no wrapper div, no positioning
 * context) so the baseline DOM is byte-identical to the pre-migration render.
 */
function BaseFieldWithTooltip({
  name,
  label,
  tooltip,
  children,
}: Readonly<{
  name: string;
  label: string;
  tooltip?: string;
  children: ReactNode;
}>) {
  if (!tooltip) return <>{children}</>;
  return (
    <div className="relative">
      <span className="absolute right-0 top-0 z-10 flex items-center">
        <FieldTooltipIcon name={name} label={label} tooltip={tooltip} />
      </span>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Removable chip — no removable-chip primitive exists, so we build one inline.
// ---------------------------------------------------------------------------

function Chip({
  label,
  onRemove,
  removeLabel,
}: Readonly<{ label: string; onRemove: () => void; removeLabel: string }>) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 text-primary-900 px-2.5 py-1 text-xs font-medium">
      <span className="break-all">{label}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        aria-label={removeLabel}
        className="!h-4 !w-4 !p-0 text-primary-700 hover:bg-primary-200"
        leftIcon={<X className="w-3 h-3" />}
      />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Recipient (À / CC) field — chip input + "+" + suggestions dropdown.
// ---------------------------------------------------------------------------

function RecipientField({
  label,
  tooltip,
  required = false,
  fieldName,
  addresses,
  suggestions,
  onAdd,
  onRemove,
}: Readonly<{
  label: string;
  tooltip?: string;
  required?: boolean;
  fieldName: string;
  addresses: string[];
  suggestions: string[];
  onAdd: (address: string) => void;
  onRemove: (address: string) => void;
}>) {
  const [draft, setDraft] = useState('');

  const commitDraft = useCallback(() => {
    const trimmed = draft.trim();
    if (!isValidEmail(trimmed)) return;
    onAdd(trimmed);
    setDraft('');
  }, [draft, onAdd]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitDraft();
      }
    },
    [commitDraft],
  );

  // Suggestions not already chosen.
  const remainingSuggestions = suggestions.filter((s) => !addresses.includes(s));
  const suggestionOptions = [
    { value: '', label: 'Suggestions' },
    ...remainingSuggestions.map((email) => ({ value: email, label: email })),
  ];

  return (
    <VStack gap={1}>
      <FieldLabel name={fieldName} label={label} tooltip={tooltip} required={required} />
      {addresses.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {addresses.map((address) => (
            <Chip
              key={address}
              label={address}
              removeLabel={`Retirer ${address}`}
              onRemove={() => {
                onRemove(address);
              }}
            />
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <TextField
          value={draft}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setDraft(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder="email@exemple.fr"
          aria-label={`${label} — ajouter une adresse`}
        />
        <Button
          variant="secondary"
          onClick={commitDraft}
          disabled={!isValidEmail(draft.trim())}
          aria-label={`Ajouter à ${label}`}
          leftIcon={<Plus className="w-4 h-4" />}
        />
      </div>
      {suggestionOptions.length > 1 && (
        <Select
          options={suggestionOptions}
          value=""
          placeholder="Suggestions"
          aria-label={`Suggestions ${label}`}
          onChange={(value) => {
            const picked = Array.isArray(value) ? value[0] : value;
            if (picked) {
              onAdd(picked);
            }
          }}
        />
      )}
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface EmailCardInnerProps extends EmailCardProps {
  /**
   * Called when the user wants to chain a NEW email after a successful send.
   * The outer component bumps a key counter to remount this inner component,
   * yielding an exact fresh-mount state (empty form, collapsed editing card,
   * active "Envoyer" button, cleared sent status).
   */
  onResetForm: () => void;
}

function EmailCardInner({
  idSin,
  dossierNumber,
  senderEmail,
  senderName,
  onResetForm,
  _layoutOverrides,
}: Readonly<EmailCardInnerProps>) {
  const { toast } = useToast();

  // Declarative field config, merged with any per-tenant layout overrides.
  // Baseline (no overrides) ⇒ `applyLayoutOverrides` returns the base config
  // unchanged (referential identity), so labels/order/visibility are
  // byte-identical to the pre-migration inline JSX.
  const baseFormConfig = useMemo(() => buildEmailFormConfig(), []);
  const formConfig = useMemo(
    () => applyLayoutOverrides(baseFormConfig, _layoutOverrides),
    [baseFormConfig, _layoutOverrides],
  );

  // A field is required when the (override-merged) config marks it so. The
  // config value is `boolean | string | undefined` — `true` uses the default
  // message, a non-empty string is a custom message (the `requireField` rule
  // contract); either way the field is required. This single predicate drives
  // both the config-driven asterisk and the generalized `canSend` gate, so the
  // displayed indicator and the submit gate can never diverge.
  const isFieldRequired = useCallback(
    (key: string): boolean => {
      const required = formConfig.fields[key]?.validation?.required;
      return required === true || typeof required === 'string';
    },
    [formConfig],
  );

  // GED add-picker placeholder, sourced from the override-merged `attachments`
  // field config (its top-level `placeholder`) — NOT an inline literal — so a
  // `setFieldPlaceholder` rule on the `attachments` field key retexts it. Falls
  // back to the baseline default if an override clears it. Used as both the
  // picker's empty-option trigger label (the visible text) and its placeholder.
  const gedPickerPlaceholder =
    formConfig.fields[FIELD.attachments]?.placeholder ?? GED_PICKER_PLACEHOLDER;

  // Headless form context so the editing form renders inside an
  // `<EntityForm>` (provider) and reads its config from there. Field VALUES
  // stay in the component state below — the form is config/layout-only here;
  // there is no EntityForm-driven submission (the send is bespoke).
  const form = useEntityForm({ config: formConfig });

  // Dropdown data
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [replyToOptions, setReplyToOptions] = useState<ReplyToOption[]>([]);
  // "En réponse à" option added for a Documents "Répondre" request (mahhp
  // 22227) — kept apart from the fetched list so the fetch resolving later can
  // never drop it, and only listed while that list does not hold the e-mail.
  const [replyFallbackOption, setReplyFallbackOption] = useState<ReplyToOption | null>(null);
  const replyToChoices = useMemo(
    () =>
      replyFallbackOption && !replyToOptions.some((o) => o.id === replyFallbackOption.id)
        ? [...replyToOptions, replyFallbackOption]
        : replyToOptions,
    [replyToOptions, replyFallbackOption],
  );
  const [recipientSuggestions, setRecipientSuggestions] = useState<string[]>([]);
  const [attachableDocuments, setAttachableDocuments] = useState<AttachableDocument[]>([]);

  // Form state
  const [replyToId, setReplyToId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [from, setFrom] = useState(senderEmail ?? '');
  // True once the user types in "De" — after that, the fetched sender must not
  // clobber their input. A ref (not state) so the async sender fetch always
  // reads the current value without re-running the mount effect.
  const fromTouchedRef = useRef(false);
  // Sender name from `WS2_GS_SEL_EXPEDITEUR`, used in `sendEmail` when the
  // `senderName` prop is absent.
  const [senderNameFromEndpoint, setSenderNameFromEndpoint] = useState('');
  const [toAddresses, setToAddresses] = useState<string[]>([]);
  const [ccAddresses, setCcAddresses] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<AttachableDocument[]>([]);
  const [subject, setSubject] = useState(
    dossierNumber ? `Dossier ${dossierNumber}` : '',
  );
  // The message is HTML — the RichTextEditor renders & emits HTML, and the
  // mail-send endpoint's `message` field is HTML.
  const [messageHtml, setMessageHtml] = useState('');
  // Latest fetched template body + origin email HTML — kept so the message can
  // be recomposed deterministically (`template + separator + origin`) when the
  // user selects a template and an origin email in either order.
  const [templateBodyHtml, setTemplateBodyHtml] = useState('');
  const [originBodyHtml, setOriginBodyHtml] = useState('');

  // Collapsed on first paint — every card in the claim view's right column
  // opens folded. Driven by state + `onExpandChange` rather than a literal
  // `defaultExpanded={false}`, because `Card` re-applies a literal default on
  // every parent re-render, which would fold the card again mid-edit.
  const [expanded, setExpanded] = useState(false);

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentToAddresses, setSentToAddresses] = useState<string[]>([]);

  // Bumped by `IntervenantForm` once an intervenant has been added to the
  // claim — the recipient suggestions below re-fetch on it (mahhp 22850).
  const intervenantsVersion = useClaimDataStore((s) => s.intervenantsVersion);

  // Fetch dropdown data on mount. Each fetch fails soft to an empty list so a
  // single failing endpoint never crashes the whole card. (The recipient
  // suggestions have their own effect below.)
  useEffect(() => {
    let active = true;

    // Sender ("De") prepopulation: the endpoint value takes precedence over the
    // `senderEmail` prop default, but only while the user hasn't edited the
    // field — never clobber typed input.
    void emailService.fetchSender().then(
      (sender) => {
        if (!active || !sender) return;
        setFrom((prev) => (fromTouchedRef.current ? prev : sender.email));
        setSenderNameFromEndpoint(sender.name);
      },
      () => { /* fail-soft: keep the prop default / empty field */ },
    );
    void emailService.fetchTemplates().then(
      (data) => { if (active) setTemplates(data); },
      () => { if (active) setTemplates([]); },
    );
    void emailService.fetchReplyToOptions(idSin).then(
      (data) => { if (active) setReplyToOptions(data); },
      () => { if (active) setReplyToOptions([]); },
    );
    void emailService.fetchAttachableDocuments(idSin).then(
      (data) => { if (active) setAttachableDocuments(data); },
      () => { if (active) setAttachableDocuments([]); },
    );

    return () => {
      active = false;
    };
  }, [idSin]);

  // "À" / "CC" recipient suggestions (`WS2_GS_SEL_EMAIL`): fetched on mount
  // and re-fetched each time an intervenant is added to the claim, so the new
  // intervenant's address is proposed without a page reload (mahhp 22850).
  // Only the suggestion list is replaced — already-chosen "À" / "CC"
  // addresses are untouched. Fails soft to an empty list, like the mount
  // fetches.
  useEffect(() => {
    let active = true;
    void emailService.fetchRecipientSuggestions(idSin).then(
      (data) => { if (active) setRecipientSuggestions(data); },
      () => { if (active) setRecipientSuggestions([]); },
    );
    return () => {
      active = false;
    };
  }, [idSin, intervenantsVersion]);

  const addTo = useCallback((address: string) => {
    setToAddresses((prev) => (prev.includes(address) ? prev : [...prev, address]));
  }, []);
  const removeTo = useCallback((address: string) => {
    setToAddresses((prev) => prev.filter((a) => a !== address));
  }, []);

  // "En réponse à" preloads the origin sender into "À" (mahhp 22859).
  // `autoAddedToRef` is the address that preload added last time — the only
  // "À" entry a reply-to change may take back out (manual addresses are never
  // touched). `replyToRequestRef` numbers the reply-to changes so a document
  // fetch that resolves after a newer change (or after clearing) leaves "À"
  // alone. `latestAddressesRef` mirrors the current "De" / "À" values for that
  // async fetch callback.
  const autoAddedToRef = useRef<string | null>(null);
  const replyToRequestRef = useRef(0);
  const latestAddressesRef = useRef({ from, toAddresses });
  useEffect(() => {
    latestAddressesRef.current = { from, toAddresses };
  }, [from, toAddresses]);

  // Removing a chip by hand disowns the preloaded sender, so re-adding the
  // same address manually afterwards makes it a manual (kept) entry.
  const removeToByUser = useCallback(
    (address: string) => {
      if (autoAddedToRef.current === address) autoAddedToRef.current = null;
      removeTo(address);
    },
    [removeTo],
  );

  /**
   * Add the origin email's sender to "À" and remember it as auto-added. Skipped
   * when the address is not a usable e-mail (empty — PDF / parse failure — or
   * an Exchange DN), when it is the "De" sender itself, or when "À" already
   * holds it (then it stays the user's). Runs from the document fetch callback,
   * i.e. after the selection's own "À" update has been committed.
   */
  const preloadSenderIntoTo = useCallback(
    (fromAddress: string) => {
      const address = fromAddress.trim();
      if (!isValidEmail(address)) return;
      const lower = address.toLowerCase();
      const { from: currentFrom, toAddresses: currentTo } = latestAddressesRef.current;
      if (currentFrom.trim().toLowerCase() === lower) return;
      if (currentTo.some((a) => a.toLowerCase() === lower)) return;
      addTo(address);
      autoAddedToRef.current = address;
    },
    [addTo],
  );

  const addCc = useCallback((address: string) => {
    setCcAddresses((prev) => (prev.includes(address) ? prev : [...prev, address]));
  }, []);
  const removeCc = useCallback((address: string) => {
    setCcAddresses((prev) => prev.filter((a) => a !== address));
  }, []);

  // Recompose the editor body whenever the template body or origin email text
  // changes (i.e. on a template / reply-to selection). Deterministic and order-
  // independent: `template (or empty editing space) + separator + origin quote`.
  useEffect(() => {
    setMessageHtml(composeReplyBody(templateBodyHtml, originBodyHtml));
  }, [templateBodyHtml, originBodyHtml]);

  // En réponse à — selecting a real option preloads the original email (#5).
  // The subject prefills with the origin subject, `RE: `-prefixed, and the body
  // quotes the origin text under a reply separator. When the document can't be
  // parsed (PDF, …) we fall back to seeding the subject from the option label
  // — or from `fallbackLabel` (the "Répondre" request's label, mahhp 22227)
  // when the option is not in the list this callback closed over yet.
  const handleReplyToChange = useCallback(
    (value: string, fallbackLabel?: string) => {
      setReplyToId(value);
      // Every change (clearing included) first takes back the sender the
      // previous selection preloaded into "À" (mahhp 22859).
      const requestId = ++replyToRequestRef.current;
      if (autoAddedToRef.current) {
        removeTo(autoAddedToRef.current);
        autoAddedToRef.current = null;
      }
      // Clearing the selection drops the quoted origin (the body recomposes
      // from the template alone) and leaves the subject untouched.
      if (!value) {
        setOriginBodyHtml('');
        return;
      }
      const optionLabel =
        replyToChoices.find((o) => o.id === value)?.label ?? fallbackLabel;
      const seedFromLabel = () => {
        if (optionLabel) setSubject(buildReplySubject(optionLabel));
        setOriginBodyHtml('');
      };
      void emailService.fetchOriginalDocument(value).then(
        (doc) => {
          // Parsed email → use the real origin subject + quoted body; otherwise
          // (empty subject/body, e.g. a PDF) fall back to the dropdown label.
          if (doc.subject || doc.body) {
            setSubject(buildReplySubject(doc.subject || optionLabel || ''));
            setOriginBodyHtml(originBodyToHtml(doc.body, doc.bodyIsHtml));
          } else {
            seedFromLabel();
          }
          // Reply to the sender: preload their address into "À" — unless a
          // newer reply-to change superseded this fetch.
          if (requestId === replyToRequestRef.current) {
            preloadSenderIntoTo(doc.fromAddress);
          }
        },
        () => {
          // Preload is best-effort; still seed the subject from the label.
          seedFromLabel();
        },
      );
    },
    [replyToChoices, removeTo, preloadSenderIntoTo],
  );

  // Utiliser un modèle — selecting a real template loads subject + body (#6).
  const handleTemplateChange = useCallback(
    (value: string) => {
      setTemplateId(value);
      if (!value) {
        // Clearing the template drops its editing space; the body recomposes
        // from the origin quote alone (or empties).
        setTemplateBodyHtml('');
        return;
      }
      void emailService.fetchTemplateContent(value, idSin).then(
        ({ subject: tplSubject, body: tplBody }) => {
          setSubject(tplSubject);
          // Fusion templates return HTML — keep it as the editing space; the
          // recompose effect prepends it above any quoted origin email.
          setTemplateBodyHtml(tplBody);
        },
        () => {
          toast({
            variant: 'error',
            title: 'Erreur',
            message: 'Impossible de charger le modèle.',
          });
        },
      );
    },
    [idSin, toast],
  );

  const addAttachment = useCallback(
    (value: string) => {
      if (!value) return;
      const doc = attachableDocuments.find((d) => d.id === value);
      if (!doc) return;
      setAttachments((prev) => (prev.some((a) => a.id === doc.id) ? prev : [...prev, doc]));
    },
    [attachableDocuments],
  );
  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // Config-driven submit gate. Each data field has a "filled" predicate keyed by
  // its stable FIELD key; a field gates the button only when the (override-
  // merged) config marks it required (`isFieldRequired`). The default required
  // set is `{to, subject}` — so this is byte-identical to the historic
  // `toAddresses.length > 0 && subject.trim().length > 0` gate — but a
  // `requireField` rule on ANY field (e.g. `cc`) now also gates the button (and
  // shows that field's asterisk). Unknown keys default to "filled" so a stray
  // required override never hard-locks the button (only the known data fields
  // gate). The submit gate stays the single send guard (no inline error text).
  const messageHasContent = messageHtml.replace(/<[^>]*>/g, '').trim().length > 0;
  const filledMap: Record<string, boolean> = {
    [FIELD.replyTo]: replyToId.trim().length > 0,
    [FIELD.template]: templateId.trim().length > 0,
    [FIELD.from]: from.trim().length > 0,
    [FIELD.to]: toAddresses.length > 0,
    [FIELD.cc]: ccAddresses.length > 0,
    [FIELD.attachments]: attachments.length > 0,
    [FIELD.subject]: subject.trim().length > 0,
    [FIELD.message]: messageHasContent,
  };
  const requiredKeys = Object.keys(formConfig.fields).filter((key) =>
    isFieldRequired(key),
  );
  const canSend =
    requiredKeys.every((key) => filledMap[key] ?? true) && !sending;

  const handleSend = useCallback(async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await emailService.sendEmail({
        idSin,
        from: from.trim() || SENDER_PLACEHOLDER,
        // The `senderName` prop wins; otherwise fall back to the name from
        // `WS2_GS_SEL_EXPEDITEUR` (`EXPEDITEUR_NOM`).
        senderName: senderName ?? senderNameFromEndpoint,
        to: toAddresses,
        cc: ccAddresses,
        subject,
        body: messageHtml,
        attachmentIds: attachments.map((a) => a.id),
      });
      setSentToAddresses(toAddresses);
      setSent(true);
      toast({
        variant: 'success',
        title: 'Email envoyé',
        message: `Envoyé à ${toAddresses.join(', ')}.`,
      });
    } catch (err) {
      const errMessage = err instanceof Error ? err.message : "Échec de l'envoi.";
      toast({
        variant: 'error',
        title: "Échec de l'envoi",
        message: errMessage,
      });
    } finally {
      setSending(false);
    }
  }, [
    canSend,
    idSin,
    from,
    senderName,
    senderNameFromEndpoint,
    toAddresses,
    ccAddresses,
    subject,
    messageHtml,
    attachments,
    toast,
  ]);

  // -------------------------------------------------------------------------
  // "Répondre" from the Documents card (mahhp 22227).
  //
  // The Documents card (or, for a click in its popout window, the claim view
  // relaying the popout's message) leaves a request in the claim-data store;
  // this card takes it: it opens itself, selects the e-mail in "En réponse à"
  // through the regular `handleReplyToChange` path (subject `RE: …`, quoted
  // body, sender preloaded into "À" — mahhp 22859; manual "À" addresses are
  // kept, like any reply-to change), and scrolls into view.
  //
  // - Another claim's request can only come from a popout gone stale: it is
  //   dropped (cleared), so it never fires later when that claim is opened.
  // - The request stays pending while a send is in flight, then applies to the
  //   form (send failed) or to a fresh one (sent).
  // - In the "sent" recap state the card first resets to a fresh form (the
  //   regular `onResetForm` remount); that fresh mount then finds the request
  //   still pending and applies it.
  // - The request may arrive before the "En réponse à" options are fetched (or
  //   name an e-mail they do not list): the e-mail is added as a fallback
  //   option, which the fetched list then matches (or keeps listing).
  // - `appliedReplyNonceRef` + clearing the request (by nonce) make sure a
  //   re-render, a re-run of this effect or a later remount never applies the
  //   same request twice.
  // -------------------------------------------------------------------------
  const replyRequest = useClaimDataStore((s) => s.emailReplyRequest);
  const consumeEmailReply = useClaimDataStore((s) => s.consumeEmailReply);
  const appliedReplyNonceRef = useRef<number | null>(null);
  // Bumped once per applied request; the scroll effect below follows it.
  const [replyScrollRequest, setReplyScrollRequest] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!replyRequest) return;
    if (replyRequest.idSin !== idSin) {
      consumeEmailReply(replyRequest.nonce);
      return;
    }
    if (appliedReplyNonceRef.current === replyRequest.nonce) return;
    if (sending) return;
    if (sent) {
      onResetForm();
      return;
    }
    appliedReplyNonceRef.current = replyRequest.nonce;
    consumeEmailReply(replyRequest.nonce);
    setReplyFallbackOption({ id: replyRequest.idAdoc, label: replyRequest.label });
    setExpanded(true);
    handleReplyToChange(replyRequest.idAdoc, replyRequest.label);
    setReplyScrollRequest((n) => n + 1);
  }, [
    replyRequest,
    idSin,
    sending,
    sent,
    onResetForm,
    consumeEmailReply,
    handleReplyToChange,
  ]);

  // Scroll the opened card into view, top first ("En réponse à" / "À" /
  // "Objet"), one frame after the request was applied: `Card` applies the
  // header's `defaultExpanded` in its own layout effect, whose update commits
  // after this passive effect (see `useActionInjection`), so the expanded
  // content is only laid out a frame later. `scrollIntoView` is guarded like
  // the launcher's (jsdom has none).
  useEffect(() => {
    if (replyScrollRequest === 0) return;
    const frame = requestAnimationFrame(() => {
      const el = cardRef.current;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [replyScrollRequest]);

  // Select option lists — derived from the fetched data + current state.
  // Memoised so the field renderer's `useCallback` deps stay stable across
  // renders (and to avoid rebuilding identical arrays each render); the inputs
  // are the same as the pre-migration inline derivations.
  const replyOptions = useMemo(
    () => [
      NONE_REPLY_OPTION,
      ...replyToChoices.map((o) => ({ value: o.id, label: o.label })),
    ],
    [replyToChoices],
  );
  const templateOptions = useMemo(
    () => [
      NONE_TEMPLATE_OPTION,
      ...templates.map((t) => ({ value: t.id, label: t.label })),
    ],
    [templates],
  );
  const attachmentOptions = useMemo(
    () => [
      // The empty option's label is the picker's visible trigger text; source it
      // from the merged `attachments` placeholder so an override is observable.
      { value: '', label: gedPickerPlaceholder },
      ...attachableDocuments
        .filter((d) => !attachments.some((a) => a.id === d.id))
        .map((d) => ({ value: d.id, label: d.label })),
    ],
    [attachableDocuments, attachments, gedPickerPlaceholder],
  );

  // Attachments DataList — inline client-side rows (the `attachments` state),
  // each carrying a fresh `onRemove` closure so the singleton row-action handler
  // always calls the current `removeAttachment`. The `columns` bucket of the
  // same `_layoutOverrides` is merged here via `applyLayoutOverrides` (the
  // host-aware merge consumes only the `columns` bucket on a DataListConfig,
  // leaving the form's `fields` bucket to the EntityForm config above). Baseline
  // (no overrides) ⇒ the config is returned unchanged (byte-identical render).
  const attachmentRows = useMemo<AttachmentRow[]>(
    () =>
      attachments.map((doc) => ({
        ...doc,
        __ctx: {
          onRemove: () => {
            removeAttachment(doc.id);
          },
        },
      })),
    [attachments, removeAttachment],
  );
  const baseAttachmentsListConfig = useMemo(
    () => buildAttachmentsListConfig(attachmentRows),
    [attachmentRows],
  );

  usePublishLayoutConfig('claimflow.emailCard', {
    ...buildPublishedConfigFromEntityForm(baseFormConfig, 'Email'),
    columns: buildPublishedConfigFromDataList(baseAttachmentsListConfig).columns,
  });

  const attachmentsListConfig = useMemo(
    () => applyLayoutOverrides(baseAttachmentsListConfig, _layoutOverrides),
    [baseAttachmentsListConfig, _layoutOverrides],
  );
  const attachmentsList = useDataList(attachmentsListConfig);

  // -------------------------------------------------------------------------
  // Config-driven field renderer.
  //
  // Each stable key dispatches to its control, reading the resolved label /
  // tooltip from the (override-merged) config. `from`/`subject` use base
  // inputs; the side-effecting selects, recipient editors, attachment picker,
  // and the bespoke editor are custom slots (label config-driven, body
  // unchanged). Returning `null` for an unknown key keeps a stray override key
  // a safe no-op.
  // -------------------------------------------------------------------------
  const renderField = useCallback(
    (name: string, field: FieldConfig): ReactNode => {
      switch (name) {
        case FIELD.replyTo:
          return (
            <BaseFieldWithTooltip name={FIELD.replyTo} label={field.label} tooltip={field.tooltip}>
              <Select
                label={field.label}
                required={isFieldRequired(FIELD.replyTo)}
                options={replyOptions}
                value={replyToId}
                onChange={(value) => {
                  handleReplyToChange(Array.isArray(value) ? (value[0] ?? '') : value);
                }}
              />
            </BaseFieldWithTooltip>
          );
        case FIELD.template:
          return (
            <BaseFieldWithTooltip name={FIELD.template} label={field.label} tooltip={field.tooltip}>
              {/* `searchable` (mahhp 22860): the template list is long, so the
                  dropdown carries the same search box as the intervenant
                  selects. Control behaviour only — no label / config key. */}
              <Select
                label={field.label}
                required={isFieldRequired(FIELD.template)}
                options={templateOptions}
                value={templateId}
                searchable
                onChange={(value) => {
                  handleTemplateChange(Array.isArray(value) ? (value[0] ?? '') : value);
                }}
              />
            </BaseFieldWithTooltip>
          );
        case FIELD.from:
          return (
            <BaseFieldWithTooltip name={FIELD.from} label={field.label} tooltip={field.tooltip}>
              {/* `from`'s declarative kind is `email` (spec field table), but
                  it is rendered as a custom slot whose base input is the same
                  bare `TextField` as before (no `type` attr) — the key + label
                  + order + tooltip are the rule contract, not the input type —
                  so the baseline DOM stays byte-identical. */}
              <TextField
                label={field.label}
                required={isFieldRequired(FIELD.from)}
                value={from}
                placeholder={SENDER_PLACEHOLDER}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  fromTouchedRef.current = true;
                  setFrom(e.target.value);
                }}
              />
            </BaseFieldWithTooltip>
          );
        case FIELD.to:
          return (
            <RecipientField
              fieldName={FIELD.to}
              label={field.label}
              tooltip={field.tooltip}
              required={isFieldRequired(FIELD.to)}
              addresses={toAddresses}
              suggestions={recipientSuggestions}
              onAdd={addTo}
              onRemove={removeToByUser}
            />
          );
        case FIELD.cc:
          return (
            <RecipientField
              fieldName={FIELD.cc}
              label={field.label}
              tooltip={field.tooltip}
              required={isFieldRequired(FIELD.cc)}
              addresses={ccAddresses}
              suggestions={recipientSuggestions}
              onAdd={addCc}
              onRemove={removeCc}
            />
          );
        case FIELD.attachments:
          return (
            <VStack gap={1}>
              <FieldLabel
                name={FIELD.attachments}
                label={field.label}
                tooltip={field.tooltip}
                required={isFieldRequired(FIELD.attachments)}
              />
              {/* Chosen attachments render via the integrated DataList so the
                  column is a rule target (`_layoutOverrides.columns`). Only
                  shown when there is at least one attachment — parity with the
                  original chip list, which rendered nothing when empty (the
                  DataList's own empty placeholder is intentionally avoided). */}
              {attachments.length > 0 && (
                <DataList config={attachmentsListConfig} list={attachmentsList}>
                  <DataList.Table />
                </DataList>
              )}
              <Select
                options={attachmentOptions}
                value=""
                // Placeholder + accessible name come from the merged `attachments`
                // field config (its top-level `placeholder`), so a
                // `setFieldPlaceholder` rule on the `attachments` key retexts them.
                placeholder={gedPickerPlaceholder}
                aria-label={gedPickerPlaceholder}
                onChange={(value) => {
                  addAttachment(Array.isArray(value) ? (value[0] ?? '') : value);
                }}
              />
            </VStack>
          );
        case FIELD.subject:
          return (
            <BaseFieldWithTooltip name={FIELD.subject} label={field.label} tooltip={field.tooltip}>
              <TextField
                label={field.label}
                required={isFieldRequired(FIELD.subject)}
                value={subject}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setSubject(e.target.value);
                }}
              />
            </BaseFieldWithTooltip>
          );
        case FIELD.message:
          return (
            <VStack gap={1}>
              <FieldLabel
                name={FIELD.message}
                label={field.label}
                tooltip={field.tooltip}
                required={isFieldRequired(FIELD.message)}
              />
              <RichTextEditor
                value={messageHtml}
                onChange={setMessageHtml}
                placeholder="Rédigez votre message..."
                aria-label="Message"
              />
            </VStack>
          );
        default:
          return null;
      }
    },
    [
      isFieldRequired,
      replyOptions,
      replyToId,
      handleReplyToChange,
      templateOptions,
      templateId,
      handleTemplateChange,
      from,
      toAddresses,
      recipientSuggestions,
      addTo,
      removeToByUser,
      ccAddresses,
      addCc,
      removeCc,
      attachments,
      attachmentOptions,
      gedPickerPlaceholder,
      addAttachment,
      attachmentsListConfig,
      attachmentsList,
      subject,
      messageHtml,
    ],
  );

  // -------------------------------------------------------------------------
  // SENT (recap) branch
  // -------------------------------------------------------------------------

  if (sent) {
    return (
      <Card key="sent">
        <Card.Header collapsible defaultExpanded={false}>
          <Icon icon={CheckCircle2} size="md" color="success" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Email', _layoutOverrides)}
          </Text>
          <Text as="span" size="sm" color="muted" className="ml-2">
            (Envoyé à {sentToAddresses.join(', ')})
          </Text>
        </Card.Header>
        <Card.Content>
          {/* Chain to a NEW email. Remounts the component (via the outer key
              counter) so the form returns to its exact initial state: empty
              recipients, default subject, collapsed editing card, and the
              active "Envoyer" submit button. */}
          <div className="pb-4">
            <Button
              variant="secondary"
              leftIcon={<RotateCcw className="w-4 h-4" />}
              onClick={onResetForm}
              className="w-full"
            >
              Nouvel email
            </Button>
          </div>
          <VStack gap={2}>
            <Text size="sm" color="muted">
              Objet
            </Text>
            <Text size="base" weight="semibold">
              {subject}
            </Text>
          </VStack>
        </Card.Content>
      </Card>
    );
  }

  // -------------------------------------------------------------------------
  // EDITING (form) branch
  // -------------------------------------------------------------------------

  const orderedFields = resolveFieldOrder(formConfig.fields);

  return (
    <Card key="open" id={actionAnchorId('claimflow.emailCard')} ref={cardRef}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
      >
        <Icon icon={Mail} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Email', _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        {/* The form is rendered field-by-field from the declarative,
            override-merged config (sorted by `order`, filtered by `visible`)
            so rename / hide / reorder / tooltip rules take effect across all
            fields. The `<EntityForm>` provider supplies the config context;
            values + side effects remain component-owned. */}
        <EntityForm config={formConfig} form={form}>
          <VStack gap={4}>
            {orderedFields.map(([name, field]) => (
              <div key={name} data-testid={`email-field-${name}`}>
                {renderField(name, field)}
              </div>
            ))}

            {formConfig.actions?.submit?.visible !== false && (
              <Button
                variant="primary"
                className="w-full"
                disabled={!canSend}
                loading={sending}
                onClick={() => void handleSend()}
                leftIcon={<Send className="w-4 h-4" />}
              >
                {formConfig.actions?.submit?.label ?? 'Envoyer'}
              </Button>
            )}
          </VStack>
        </EntityForm>
      </Card.Content>
    </Card>
  );
}

export function EmailCard(props: Readonly<EmailCardProps>) {
  // Remount key. Bumping it after a successful send reloads the inner component
  // to its initial state, letting the user chain a NEW email. A full remount
  // (rather than resetting each field) is used because there are many pieces of
  // local state (recipients, subject, body, sent flag, fetched dropdowns), so a
  // remount is the only way to guarantee an exact fresh-mount state.
  const [resetKey, setResetKey] = useState(0);

  const handleResetForm = useCallback(() => {
    setResetKey((k) => k + 1);
  }, []);

  return (
    <EmailCardInner key={resetKey} {...props} onResetForm={handleResetForm} />
  );
}
